import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, Loader2, X, Upload } from "lucide-react";
import { api, formatApiError, imgSrc } from "@/lib/api";

const emptyProduct = {
  name: "", slug: "", crop: "Maize", category: "", short_description: "", description: "",
  image: "", pack_size: "25 kg", region: "Bihar", published: true, order: 0,
};

const specsToRows = (specs) => Object.entries(specs || {}).map(([key, value]) => ({ key, value }));
const rowsToSpecs = (rows) => Object.fromEntries(rows.filter((r) => r.key.trim()).map((r) => [r.key.trim(), r.value]));

export default function ProductsManager() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [specRows, setSpecRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/products");
      setProducts(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startEdit = (p) => {
    setEditing(p ? { ...p } : { ...emptyProduct });
    setSpecRows(p ? specsToRows(p.specs) : []);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...editing, specs: rowsToSpecs(specRows) };
    delete payload.id;
    delete payload.created_at;
    try {
      if (editing.id) {
        await api.put(`/admin/products/${editing.id}`, payload);
        toast.success("Product updated.");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Product created.");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this product permanently?")) return;
    try {
      await api.delete(`/admin/products/${id}`);
      toast.success("Product deleted.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/media", fd);
      setEditing((ed) => ({ ...ed, image: data.url }));
      toast.success("Image uploaded and attached.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const set = (k) => (e) => setEditing((ed) => ({ ...ed, [k]: e.target.value }));

  if (loading) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 text-brand-deep animate-spin" /></div>;

  return (
    <div data-testid="products-manager">
      {!editing ? (
        <>
          <div className="flex justify-between items-center mb-6">
            <h2 className="font-display text-xl font-extrabold text-brand-deep">Products ({products.length})</h2>
            <button onClick={() => startEdit(null)} data-testid="product-new-button" className="flex items-center gap-2 rounded-full bg-brand-deep px-5 py-2.5 text-xs font-bold text-white hover:bg-brand-forest transition-all">
              <Plus className="w-4 h-4" /> New Product
            </button>
          </div>
          <div className="space-y-3">
            {products.map((p) => (
              <div key={p.id} data-testid={`admin-product-${p.slug}`} className="bg-white rounded-2xl border border-brand-deep/10 p-4 flex items-center gap-4">
                <img src={imgSrc(p.image)} alt={p.name} className="w-14 h-14 rounded-xl object-cover bg-brand-beige shrink-0" />
                <div className="flex-1 min-w-0">
                  <h3 className="font-display font-bold text-brand-deep truncate">{p.name} <span className="text-xs font-body font-medium text-brand-charcoal/40">· {p.crop} · {p.pack_size}</span></h3>
                  <p className="text-xs text-brand-charcoal/50 truncate">{p.short_description}</p>
                </div>
                <span className={`text-[10px] font-bold uppercase px-2.5 py-1 rounded-full ${p.published ? "bg-brand-deep/10 text-brand-deep" : "bg-slate-100 text-slate-400"}`}>
                  {p.published ? "Live" : "Hidden"}
                </span>
                <button onClick={() => startEdit(p)} data-testid={`product-edit-${p.slug}`} className="w-9 h-9 rounded-full bg-brand-deep/5 border border-brand-deep/15 flex items-center justify-center text-brand-deep hover:bg-brand-deep hover:text-white transition-all">
                  <Pencil className="w-4 h-4" />
                </button>
                <button onClick={() => remove(p.id)} data-testid={`product-delete-${p.slug}`} className="w-9 h-9 rounded-full bg-red-50 border border-red-200 flex items-center justify-center text-red-500 hover:bg-red-500 hover:text-white transition-all">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </>
      ) : (
        <form onSubmit={save} data-testid="product-editor-form" className="bg-white rounded-3xl border border-brand-deep/10 p-6 sm:p-8 space-y-5">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl font-extrabold text-brand-deep">{editing.id ? "Edit Product" : "New Product"}</h2>
            <button type="button" onClick={() => setEditing(null)} data-testid="product-editor-cancel" className="w-9 h-9 rounded-full bg-brand-deep/5 flex items-center justify-center text-brand-deep hover:bg-brand-deep hover:text-white transition-all">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="label-field">Name *</label>
              <input required className="input-field" data-testid="product-name-input" value={editing.name} onChange={set("name")} />
            </div>
            <div>
              <label className="label-field">Slug *</label>
              <input required className="input-field" data-testid="product-slug-input" value={editing.slug} onChange={set("slug")} placeholder="sipra-6655" />
            </div>
            <div>
              <label className="label-field">Crop</label>
              <select className="input-field" data-testid="product-crop-select" value={editing.crop} onChange={set("crop")}>
                <option>Maize</option>
                <option>Wheat</option>
                <option>Other</option>
              </select>
            </div>
            <div>
              <label className="label-field">Category</label>
              <input className="input-field" data-testid="product-category-input" value={editing.category} onChange={set("category")} placeholder="Premium Maize Seed" />
            </div>
            <div>
              <label className="label-field">Pack Size</label>
              <input className="input-field" value={editing.pack_size} onChange={set("pack_size")} />
            </div>
            <div>
              <label className="label-field">Region</label>
              <input className="input-field" value={editing.region} onChange={set("region")} />
            </div>
            <div>
              <label className="label-field">Display Order</label>
              <input type="number" className="input-field" value={editing.order} onChange={set("order")} />
            </div>
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="product-published-toggle"
                  checked={editing.published}
                  onChange={(e) => setEditing((ed) => ({ ...ed, published: e.target.checked }))}
                  className="w-5 h-5 rounded accent-[#14532D]"
                />
                <span className="text-sm font-semibold text-brand-deep">Published (visible on website)</span>
              </label>
            </div>
          </div>
          <div>
            <label className="label-field">Short Description</label>
            <input className="input-field" value={editing.short_description} onChange={set("short_description")} />
          </div>
          <div>
            <label className="label-field">Full Description</label>
            <textarea rows={4} className="input-field" value={editing.description} onChange={set("description")} />
          </div>
          <div>
            <label className="label-field">Image URL</label>
            <div className="flex gap-2">
              <input className="input-field flex-1" data-testid="product-image-input" value={editing.image} onChange={set("image")} placeholder="https://… or /api/uploads/…" />
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadImage} />
              <button type="button" onClick={() => fileRef.current?.click()} data-testid="product-image-upload" className="flex items-center gap-2 rounded-xl bg-brand-deep/5 border border-brand-deep/15 px-4 text-xs font-bold text-brand-deep hover:bg-brand-deep hover:text-white transition-all">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload
              </button>
            </div>
            {editing.image && <img src={imgSrc(editing.image)} alt="Preview" className="mt-3 h-28 rounded-xl object-cover" />}
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="label-field !mb-0">Technical Specifications</label>
              <button type="button" onClick={() => setSpecRows((r) => [...r, { key: "", value: "" }])} data-testid="spec-add-row" className="flex items-center gap-1.5 text-xs font-bold text-brand-deep hover:text-brand-gold transition-colors">
                <Plus className="w-3.5 h-3.5" /> Add Field
              </button>
            </div>
            <div className="space-y-2">
              {specRows.map((row, i) => (
                <div key={i} className="flex gap-2">
                  <input className="input-field !py-2 text-xs w-2/5" placeholder="Field (e.g. Maturity)" value={row.key} onChange={(e) => setSpecRows((rows) => rows.map((r, j) => (j === i ? { ...r, key: e.target.value } : r)))} />
                  <input className="input-field !py-2 text-xs flex-1" placeholder="Value" value={row.value} onChange={(e) => setSpecRows((rows) => rows.map((r, j) => (j === i ? { ...r, value: e.target.value } : r)))} />
                  <button type="button" onClick={() => setSpecRows((rows) => rows.filter((_, j) => j !== i))} className="w-9 shrink-0 rounded-xl bg-red-50 text-red-400 flex items-center justify-center hover:bg-red-500 hover:text-white transition-all">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
          <button type="submit" disabled={saving} data-testid="product-save-button" className="rounded-full bg-brand-deep px-8 py-3.5 text-sm font-bold text-white hover:bg-brand-forest transition-all disabled:opacity-60 flex items-center gap-2">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save Product
          </button>
        </form>
      )}
    </div>
  );
}
